import { GetProps, Result } from "@/api/types";
import { dateFormat, useDateRange } from "@/pages/dashboard/hooks/useDateRange";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { IImpact, ImpactMetricKey } from "../utils/types";

interface GetCompanyImpactTotals extends Result {
  result: {
    company_impact: IImpact;
    chapters: ({ label: string } & IImpact)[];
  };
}

export function useCompanyImpactTotals({ metrics }: { metrics: ImpactMetricKey[] }) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: ["dashboard", "company", "companyImpactTotals", startDate, endDate, metrics],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .get<GetCompanyImpactTotals>("/dashboard/company/totals", {
          params: {
            end_date: endDate?.format(dateFormat),
            start_date: startDate?.format(dateFormat),
            metrics: metrics.join(";"),
          },
        })
        .then((result) => result.data.result),
  });

  return query;
}

interface GetCompanyImpactTopContributors extends Result {
  result: {
    contributors: {
      label: string;
      type: string;
      subtype: string;
      impact: IImpact;
    }[];
    total_rows: number;
    company_impact: IImpact;
  };
}

export function useCompanyImpactTopContributors({
  page,
  pageSize,
  activeFilters,
  activeSorting,
  metric,
}: GetProps) {
  const { startDate, endDate } = useDateRange();

  const query = useQuery({
    queryKey: [
      "dashboard",
      "company",
      "companyImpactTopContributors",
      startDate,
      endDate,
      page,
      pageSize,
      activeFilters,
      activeSorting,
      metric,
    ],
    enabled: !!startDate && !!endDate,
    queryFn: () =>
      axios
        .post<GetCompanyImpactTopContributors>(
          "/dashboard/company/top_contributors",
          {
            filters: activeFilters,
            sorter: activeSorting,
          },
          {
            params: {
              page,
              pageSize,
              end_date: endDate?.format(dateFormat),
              start_date: startDate?.format(dateFormat),
              metric: metric,
            },
          }
        )
        .then((result) => result.data.result),
  });

  return query;
}
